/**
 * Gating screen shown until the user is fully signed in.
 *
 * Three sub-states it can be rendering:
 *   - booting:   spinner while we verify initData and look up the stored JWT
 *   - not_in_tg: hard error — the app must be opened from Telegram
 *   - needs_wallet / verifying_wallet: TonConnect button + status line
 *   - error: surface the error message with a retry hint
 *
 * The actual "Connect Wallet" button comes from `@tonconnect/ui-react`'s
 * `TonConnectButton`. We also drive the Telegram MainButton in parallel so
 * users get the native bottom CTA when running inside TG — the SDK's UI
 * sometimes lives mid-screen which feels off in a TMA.
 */
import { useEffect, useState } from "react";
import {
  TonConnectButton,
  useTonConnectModal,
  useTonConnectUI,
} from "@tonconnect/ui-react";
import { useAuth } from "../hooks/useAuth";
import { mainButton, isInTelegram } from "../lib/telegram";
import { SpinnerIcon, WalletIcon } from "../components/Icons";

export function ConnectPage(): JSX.Element {
  const { status, errorMessage } = useAuth();
  const { open: openModal, state: modalState } = useTonConnectModal();
  const [tonConnectUI] = useTonConnectUI();
  const [debugLog, setDebugLog] = useState<string[]>([]);
  // Back to URL-flag-only after diagnosis: open with ?debug=1 to re-enable.
  const debug =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).has("debug");

  // Tap every TonConnect status change into the debug log.
  useEffect(() => {
    if (!debug) return;
    const unsub = tonConnectUI.onStatusChange(
      (w) => {
        setDebugLog((prev) => [
          ...prev,
          w === null
            ? "onStatusChange: disconnected"
            : `onStatusChange: connected ${w.account.address.slice(0, 8)}… proof=${"connectItems" in w && w.connectItems?.tonProof !== undefined ? "yes" : "no"}`,
        ]);
      },
      (err) => {
        setDebugLog((prev) => [...prev, `onStatusChange ERROR: ${String(err)}`]);
      },
    );
    setDebugLog((prev) => [
      ...prev,
      `boot: tonConnectUI ready, modal=${modalState.status}`,
    ]);
    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debug, tonConnectUI]);

  useEffect(() => {
    if (!debug) return;
    setDebugLog((prev) => [...prev, `modal state -> ${modalState.status}`]);
  }, [debug, modalState.status]);

  useEffect(() => {
    if (!debug) return;
    setDebugLog((prev) => [...prev, `auth status -> ${status}`]);
  }, [debug, status]);

  useEffect(() => {
    if (!debug) return;
    const onDebug = (e: Event): void => {
      const ev = e as CustomEvent<string>;
      setDebugLog((prev) => [...prev, String(ev.detail)]);
    };
    window.addEventListener("tg-debug", onDebug);
    return () => window.removeEventListener("tg-debug", onDebug);
  }, [debug]);

  // We deliberately do NOT wire Telegram's MainButton for the connect step.
  // The native MainButton click loses the browser user-gesture context by the
  // time it reaches our JS handler (native button → TG bridge → webview JS,
  // several hops). TonConnect's wallet launch requires a synchronous gesture
  // to call window.open/openLink without being silently blocked. The DOM
  // <TonConnectButton /> rendered below preserves the gesture and works.
  // We still mark MainButton as "loading" during booting/verifying so the
  // user sees activity, but never bind it to the connect action.
  useEffect(() => {
    if (!isInTelegram()) return;
    mainButton.setLoading(status === "verifying_wallet" || status === "booting");
  }, [status]);

  if (status === "not_in_tg") {
    return <NotInTelegram />;
  }

  if (status === "booting") {
    return (
      <div className="flex h-full items-center justify-center">
        <SpinnerIcon size={24} className="text-tg-hint" />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col items-center justify-center gap-6 px-8 text-center">
      <div className="rounded-full bg-tg-secondary-bg p-5 text-tg-accent">
        <WalletIcon size={32} />
      </div>
      <div className="space-y-2">
        <h1 className="text-[22px] font-semibold text-tg-text">Welcome to Tolkgram</h1>
        <p className="max-w-xs text-sm leading-relaxed text-tg-hint">
          The on-chain inbox for TON wallets.
          <br />
          Connect your wallet to read and reply to messages sent as TON
          transfer comments.
        </p>
      </div>

      <div className="flex flex-col items-center gap-3">
        {/* TonConnect's DOM button — works in both web and Telegram webview
            because the click handler runs synchronously with the user gesture. */}
        <TonConnectButton />
        {status === "verifying_wallet" ? (
          <div className="flex items-center gap-2 text-sm text-tg-hint">
            <SpinnerIcon size={14} />
            Verifying wallet…
          </div>
        ) : null}
        {errorMessage !== null ? (
          <p className="max-w-xs text-xs text-tg-destructive">{errorMessage}</p>
        ) : null}
      </div>

      <p className="absolute bottom-6 max-w-xs text-[11px] text-tg-hint">
        We never see your seed or private key. The wallet signs every transfer.
      </p>

      {debug ? (
        <div
          className="fixed bottom-2 left-2 right-2 max-h-44 overflow-y-auto rounded border-2 border-red-500 bg-yellow-50 p-2 text-[10px] text-black"
          style={{ zIndex: 2147483646, pointerEvents: "none" }}
        >
          <div className="font-bold">debug (click-through)</div>
          {debugLog.slice(-16).map((line, i) => (
            <div key={i} className="font-mono">
              {line}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

const notInTgCopy =
  "Tolkgram is a Telegram Mini App. Open it from inside Telegram via the bot link to sign in. If you opened this in a browser tab, copy the URL into your Telegram chat with @TolkgramBot.";

function NotInTelegram(): JSX.Element {
  const { devSignIn, errorMessage } = useAuth();
  // ?debug=1 flips the dev-skip button on in prod builds. Pair with
  // NODE_ENV=development on the api-worker so /auth/dev-session doesn't 404.
  const debugFlag =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).has("debug");
  const isDev = import.meta.env.DEV || debugFlag;
  return (
    <div className="flex h-full flex-col items-center justify-center gap-4 px-8 text-center">
      <h1 className="text-[20px] font-semibold text-tg-text">
        Open me in Telegram
      </h1>
      <p className="max-w-xs text-sm leading-relaxed text-tg-hint">
        {notInTgCopy}
      </p>
      {isDev ? (
        <div className="mt-4 flex flex-col items-center gap-2">
          <button
            type="button"
            onClick={() => void devSignIn()}
            className="rounded-full bg-tg-button px-5 py-2 text-sm font-medium text-tg-button-text"
          >
            Continue (dev — skip Telegram)
          </button>
          <p className="max-w-xs text-[11px] text-tg-hint">
            Skips the Telegram check. You still connect a wallet via TonConnect —
            that wallet becomes your inbox. Active only when{" "}
            <code>NODE_ENV=development</code>.
          </p>
        </div>
      ) : null}
      {errorMessage !== null ? (
        <p className="max-w-xs text-xs text-tg-destructive">{errorMessage}</p>
      ) : null}
    </div>
  );
}
